import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Paper, Button, Box, CircularProgress } from '@mui/material';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';
import { apiService } from "../components/ReasuableFuncations/api";
import pptxgen from "pptxgenjs";
import html2canvas from 'html2canvas';

const ProjectAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    budget: {
      labels: [],
      datasets: []
    },
    outcome: {
      labels: [],
      datasets: []
    },
    output: {
      labels: [],
      datasets: []
    },
    activity: {
      labels: [],
      datasets: []
    }
  });

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData) throw new Error('User data not found');

      const { selectedOption, regionalNetworkOption, countryOption } = userData;
      let baseQuery = '';

      if (selectedOption === "Comesa") {
        baseQuery = '';
      } else if (selectedOption === "Regional Network" && regionalNetworkOption) {
        baseQuery = `?regionalNetwork=${regionalNetworkOption}`;
      } else if (selectedOption === "Member State" && countryOption) {
        baseQuery = `?country=${countryOption}`;
      }

      const [budgetRes, dataCollectionRes] = await Promise.all([
        apiService.get(`/api/budgets${baseQuery}`),
        apiService.get(`/api/data-collection${baseQuery}`)
      ]);

      if (!budgetRes.data || !dataCollectionRes.data) {
        throw new Error('Failed to fetch complete data');
      }

      // Process data collection results
      const outcomes = dataCollectionRes.data.filter(item => item.type === 'outcome');
      const outputs = dataCollectionRes.data.filter(item => item.type === 'output');
      const activities = dataCollectionRes.data.filter(item => item.type === 'activity');

      setData({
        budget: formatBudgetData(budgetRes.data),
        outcome: formatAchievementData(outcomes, 'Outcome Achievement Levels'),
        output: formatAchievementData(outputs, 'Output Achievement Levels'),
        activity: formatCostVarianceData(activities)
      });

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const formatBudgetData = (budgetData) => {
    if (!budgetData.length) return { labels: [], datasets: [] };
    
    const sortedData = budgetData.sort((a, b) => new Date(a.budgetDate) - new Date(b.budgetDate));
    return {
      labels: sortedData.map(b => new Date(b.budgetDate).toLocaleDateString()),
      datasets: [
        {
          label: 'Expected Budget',
          data: sortedData.map(b => b.expectedBudget || 0),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
        },
        {
          label: 'Actual Budget',
          data: sortedData.map(b => b.actualBudget || 0),
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: true,
        }
      ]
    };
  };

  const formatAchievementData = (items, label) => {
    const processedData = items.map(item => ({
      name: item.name || item.activityname,
      achievement: parseFloat(item.percentageAchievement?.replace('%', '')) || 0
    }));

    return {
      labels: processedData.map(item => item.name),
      datasets: [{
        label,
        data: processedData.map(item => item.achievement),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };
  };

  const formatCostVarianceData = (activities) => {
    const processedData = activities.map(activity => ({
      name: activity.activityname,
      variance: parseFloat(activity.percentageAchievement?.replace('%', '')) || 0
    }));

    return {
      labels: processedData.map(item => item.name),
      datasets: [{
        label: 'Cost Variance (%)',
        data: processedData.map(item => item.variance),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }]
    };
  };

  const exportToPPT = async () => {
    try {
      const pptx = new pptxgen();
      
      // Capture charts
      const budgetChart = await html2canvas(document.querySelector('.budget-chart'));
      const outcomeChart = await html2canvas(document.querySelector('.outcome-chart'));
      const outputChart = await html2canvas(document.querySelector('.output-chart'));
      const activityChart = await html2canvas(document.querySelector('.activity-chart'));
      
      // Add slides for each chart
      const addChartSlide = (title, chartData) => {
        const slide = pptx.addSlide();
        slide.addText(title, {
          x: 1,
          y: 0.5,
          fontSize: 18,
          bold: true,
          color: '363636'
        });
        slide.addImage({
          data: chartData.toDataURL(),
          x: 0.5,
          y: 1,
          w: 9,
          h: 4
        });
      };

      // Add all slides
      addChartSlide("Budget Analysis", budgetChart);
      addChartSlide("Outcome Achievement Analysis", outcomeChart);
      addChartSlide("Output Achievement Analysis", outputChart);
      addChartSlide("Activity Cost Variance Analysis", activityChart);

      await pptx.writeFile('Project_Analysis_Report.pptx');
    } catch (error) {
      console.error('Error generating PPT:', error);
      alert('Failed to generate PPT. Please try again.');
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: value => `${value}%`
        }
      }
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom align="center">
        Project Analysis Dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Budget Analysis
            </Typography>
            <Box sx={{ height: 300 }} className="budget-chart">
              <Line data={data.budget} options={{ maintainAspectRatio: false }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Outcome Achievement Analysis
            </Typography>
            <Box sx={{ height: 300 }} className="outcome-chart">
              <Bar data={data.outcome} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Output Achievement Analysis
            </Typography>
            <Box sx={{ height: 300 }} className="output-chart">
              <Bar data={data.output} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Activity Cost Variance Analysis
            </Typography>
            <Box sx={{ height: 300 }} className="activity-chart">
              <Bar data={data.activity} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button variant="contained" color="primary" onClick={exportToPPT}>
          Export to PPT
        </Button>
      </Box>
    </Container>
  );
};

export default ProjectAnalysis;
